/**
 * usePurchaseLesson — POST /lessons/purchase. Plain `useMutation`; resolves
 * the envelope unchanged and the call site branches on `success`
 * (docs/conventions/internalapi-envelope.md). Read `isPending` from the
 * mutation — never mirror it into state.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '~/lib/query/keys';
import { useClubId, useTenantHandle } from '~/services/appRuntime';
import { purchaseLesson } from '../api/purchaseLesson';
import type { PurchaseLessonRequest } from '../types';

export function usePurchaseLesson() {
  const queryClient = useQueryClient();
  const [clubIdParam] = useClubId();
  const [tenantHandle] = useTenantHandle();
  const clubId = clubIdParam ? Number(clubIdParam) : null;

  return useMutation({
    mutationFn: (request: PurchaseLessonRequest) => {
      if (!clubId) throw new Error('clubId is required');
      return purchaseLesson(request, { clubId, tenantHandle: tenantHandle || undefined });
    },
    onSuccess: (result) => {
      if (!result.success || !clubId) return;
      // Prefix match — covers the catalogue and any holdings query under it.
      queryClient.invalidateQueries({ queryKey: queryKeys.lessons.all(clubId) });
      // The subscription read carries the member's packs under `holdings` too.
      queryClient.invalidateQueries({ queryKey: queryKeys.subscriptions.all(clubId) });
    },
    // One attempt per gesture — a retried POST is a second charge.
    retry: false,
  });
}
